import { useEffect } from 'react';
import useWebSocket from 'react-use-websocket';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { config } from '@/lib/env';
import type { ReportType } from '@/services/api/reports';

export interface WsTrade {
  accountHolderId: string;
  amount: string;
  instrument: string;
  /** Not emitted by the current backend — legacy field, present only on old messages */
  orderId?: string;
  price: string;
  side: 'BUY' | 'SELL';
  /** Wire sends this as a string (confirmed against the emitting lambda) */
  time: string;
  tradeId: string;
}

export type BopWsMessage =
  | { type: 'instantTrade'; data: WsTrade }
  // Legacy variant — the backend no longer emits it; tolerated for safety
  | { type: 'trade'; data: WsTrade }
  | { type: 'fileDownload'; url: string }
  // Report workers (bop-api core/reporting/reportNotifications.js) announce a finished S3 report
  // to the requesting operator; `key` is what listReports returns, `url` is a presigned download
  | { type: 'reportReady'; reportType: ReportType; key: string; url: string; year?: number; summary?: Record<string, unknown> }
  | { type: 'reportFailed'; reportType: ReportType; error: string; year?: number };

export type FileDownloadPush = Extract<BopWsMessage, { type: 'fileDownload' }>;

export const isFileDownloadPush = (message: BopWsMessage): message is FileDownloadPush => message.type === 'fileDownload';

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * One shared connection for the whole app (`share: true` — every hook consumer
 * multiplexes the same socket). Auth is the same bearer token the HTTP client
 * uses, passed as a query param (bop-api's WS authorizer contract). The server
 * is broadcast-only: there is no subscribe protocol, clients filter messages.
 */
export const useBopWebSocket = () => {
  const token = useAuthStore((state) => state.token);
  const url = token ? `${config.BOP_WS_BASE_URL}?authorization=${encodeURIComponent(token)}` : null;

  return useWebSocket<BopWsMessage | null>(url, {
    share: true,
    shouldReconnect: () => true,
    reconnectAttempts: Number.POSITIVE_INFINITY,
    reconnectInterval: (attempt) => Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS),
  });
};

// Singleton guard for the download side effect: `share: true`
// delivers the SAME MessageEvent instance to every mounted listener, so the
// first effect to see a push claims it here and co-mounted listeners (e.g.
// forceMounted user-console tabs) skip — one export, one download, one toast.
// (`lastJsonMessage` is parsed per hook instance, so the guard must key on the
// underlying event, not the parsed object.)
let lastHandledDownloadEvent: MessageEvent | null = null;

/**
 * Async server-generated exports: the caller POSTs /v1/download (see
 * services/api/downloads.ts), bop-api builds the CSV, uploads it to S3 and
 * pushes the presigned URL here (only to the requesting operator's sockets).
 * Safe to mount on every surface that offers an export — the module-level guard
 * makes the download side effect a singleton per push.
 */
export const useFileDownloadListener = () => {
  const { lastJsonMessage, lastMessage } = useBopWebSocket();

  useEffect(() => {
    if (lastJsonMessage && 'type' in lastJsonMessage && lastJsonMessage.type === 'fileDownload' && lastJsonMessage.url) {
      if (lastMessage === null || lastMessage === lastHandledDownloadEvent) return;
      lastHandledDownloadEvent = lastMessage;
      const anchor = document.createElement('a');
      anchor.href = lastJsonMessage.url;
      anchor.rel = 'noreferrer';
      anchor.click();
      toast.success('Export ready — download started');
    }
  }, [lastJsonMessage, lastMessage]);
};
